import React, { FC, useEffect, useRef, useState } from "react";

import classnames from "classnames";
import { twMerge } from "tailwind-merge";

export type TransitionTimeout = 0 | 75 | 100 | 150 | 200 | 300 | 500 | 700 | 1000;

export interface CollapseProps {
  /** Show the component; triggers the expand or collapse animation */
  in?: boolean;
  /** Wait until the first "enter" transition to mount the component (add it to the DOM) */
  mountOnEnter?: boolean;
  /** Unmount the component (remove it from the DOM) when it is collapsed */
  unmountOnExit?: boolean;
  /** Run the expand animation when the component mounts, if it is initially shown */
  appear?: boolean;
  /** Duration of the collapse animation in milliseconds */
  timeout?: TransitionTimeout;
  /** Callback fired before the component expands */
  onEnter?: () => void;
  /** Callback fired after the component starts to expand */
  onEntering?: () => void;
  /** Callback fired after the component has expanded */
  onEntered?: () => void;
  /** Callback fired before the component collapses */
  onExit?: () => void;
  /** Callback fired after the component starts to collapse */
  onExiting?: () => void;
  /** Callback fired after the component has collapsed */
  onExited?: () => void;
  /** The dimension used when collapsing (only 'height' is supported) */
  dimension?: "height";
  /** Function that returns the height of the animating DOM node */
  getDimensionValue?: (dimension: "height", element: HTMLElement) => number;
  /** ARIA role of collapsible element */
  role?: string;
  /** Additional CSS classes to apply to the collapse container */
  className?: string;
  /** Content to be collapsed */
  children?: React.ReactNode;
}

const durationClasses: Record<TransitionTimeout, string> = {
  0: "duration-0",
  75: "duration-75",
  100: "duration-100",
  150: "duration-150",
  200: "duration-200",
  300: "duration-300",
  500: "duration-500",
  700: "duration-700",
  1000: "duration-1000",
};

/**
 * Collapse component
 * Built using Tailwind and DaisyUI collapse
 * Manages its own transitions (no dependency on react-transition-group)
 *
 * @see https://daisyui.com/components/collapse/
 */
export const Collapse: FC<CollapseProps> = ({
  in: inProp = false,
  mountOnEnter = false,
  unmountOnExit = false,
  timeout = 300,
  onEnter,
  onEntering,
  onEntered,
  onExit,
  onExiting,
  onExited,
  dimension = "height",
  getDimensionValue,
  role,
  className,
  children,
  ...props
}) => {
  const contentRef = useRef<HTMLDivElement>(null);

  // tracks whether the component has been mounted
  const [hasMounted, setHasMounted] = useState<boolean>(inProp);
  const [maxHeight, setMaxHeight] = useState<string>(inProp ? "none" : "0px");

  useEffect(() => {
    const node = contentRef.current;
    if (!node) return;

    let timeoutId: ReturnType<typeof setTimeout>;

    if (inProp) {
      if (mountOnEnter && !hasMounted) {
        setHasMounted(true);
      }

      const dimensionValue = getDimensionValue ? `${getDimensionValue(dimension, node)}px` : `${node.scrollHeight}px`;

      onEnter && onEnter();
      setMaxHeight(dimensionValue);

      // Start the expanding transition
      requestAnimationFrame(() => {
        setMaxHeight(dimensionValue);
        onEntering && onEntering();
      });

      // After the transition
      timeoutId = setTimeout(() => {
        setMaxHeight("none"); // Remove max-height restriction after expansion
        onEntered && onEntered();
      }, timeout);
    } else {
      const dimensionValue = getDimensionValue ? `${getDimensionValue(dimension, node)}px` : `${node.scrollHeight}px`;

      onExit && onExit();
      setMaxHeight(dimensionValue);

      // Force reflow to ensure the transition starts from the current height
      node.offsetHeight;

      // Start the collapsing transition
      requestAnimationFrame(() => {
        setMaxHeight("0px");
        onExiting && onExiting();
      });

      // After the transition
      timeoutId = setTimeout(() => {
        onExited && onExited();
        if (unmountOnExit) {
          setHasMounted(false);
        }
      }, timeout);
    }

    return () => clearTimeout(timeoutId);
  }, [inProp, mountOnEnter, dimension, getDimensionValue, onEnter, onEntering, onEntered, onExit, onExiting, onExited, hasMounted, timeout]); // <--- Added `hasMounted` to dependencies

  if (!inProp && !hasMounted && mountOnEnter) {
    return null;
  }

  const collapseClasses = twMerge("cobalt si-collapse overflow-hidden", className);

  const contentStyle: React.CSSProperties = {
    maxHeight,
  };

  return (
    <div className={collapseClasses} role={role} aria-expanded={inProp} {...props}>
      <div ref={contentRef} style={contentStyle} className={classnames("transition-[max-height]", durationClasses[timeout])}>
        {children}
      </div>
    </div>
  );
};

export default Collapse;
